import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Activity, BellRing, Smartphone, CheckCircle, Zap, Globe, Loader2, CheckCircle2, XCircle, Terminal, Target, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UptimeWingLogo } from "@/components/shared/UptimeWingLogo";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { WaitlistModal } from "@/components/shared/WaitlistModal";
import { useAuth } from "@/hooks/useAuth";
import { PulseMap } from "@/components/landing/PulseMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";

import { BrandName } from "@/components/shared/BrandName";
import { LandingHeader } from "@/components/layout/LandingHeader";
import { LandingHero } from "@/components/landing/LandingHero";
import { LandingAgitate } from "@/components/landing/LandingAgitate";
import { LandingShift } from "@/components/landing/LandingShift";
import { LandingMechanism } from "@/components/landing/LandingMechanism";
import { LandingProof } from "@/components/landing/LandingProof";
import { LandingPricing } from "@/components/landing/LandingPricing";
import { LandingClose } from "@/components/landing/LandingClose";
import { LandingDemo } from "@/components/landing/LandingDemo";
import {
  annualMonthlyPrice,
  annualTotalPrice,
  annualDollarSavings,
  monthlyDollarSavings,
  annualMonthlyStandard,
} from "@/lib/pricing";
import { usePricingTiers, STANDARD_TIER } from "@/hooks/usePricingTiers";

import { LandingDualMode } from "@/components/landing/LandingDualMode";

export default function LandingPage() {
  const { user, signInWithGoogle, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if the user has just logged in/signed up (via Google OAuth redirect callback)
  useEffect(() => {
    if (!loading && user) {
      const justLoggedIn = localStorage.getItem("just_clicked_login");
      if (justLoggedIn === "true") {
        localStorage.removeItem("just_clicked_login");
        navigate("/dashboard");
      }
    }
  }, [user, loading, navigate]);

  // Support scroll-to-hash on landing page initialization
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const targetId = hash.replace("#", "");
      const el = document.getElementById(targetId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  }, []);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("founder");
  const [authLoading, setAuthLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const {
    spotsLeft,
    paidCount,
    currentTier,
    nextTier,
    spotsAtThisTier,
    spotsUntilNextTier,
    previousTierMax,
    isLoading,
    isFull
  } = usePricingTiers();

  const paidUsersCount = paidCount ?? 0;
  const activePrice = currentTier ? currentTier.price : 4.99;
  const isEarlyBird = currentTier ? currentTier.label === "Early Bird" : true;
  const currentMaxSlots = currentTier ? currentTier.maxSlots : 20;
  const previousMax = previousTierMax;
  const tierProgress = currentMaxSlots === Infinity
    ? 100
    : Math.min(100, Math.max(0, ((paidUsersCount - previousMax) / (currentMaxSlots - previousMax)) * 100));

  const handleSecureFounderSpot = async () => {
    if (!user) {
      signInWithGoogle();
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("dodo-checkout", {
        body: {
          return_url: window.location.origin + "/dashboard"
        }
      });
      if (error) throw error;
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      console.error("Checkout creation failed:", err);
    } finally {
      setCheckoutLoading(false);
    }
  };


  const [scanUrl, setScanUrl] = useState("");
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanStep, setScanStep] = useState(0); // 0: input/loading, 1: results
  const [loadingStep, setLoadingStep] = useState(0); // 0 to 4
  const [activeStep, setActiveStep] = useState(0);
  const [scanResult, setScanResult] = useState<any>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    setAuthLoading(true);
    localStorage.setItem("just_clicked_login", "true");
    try {
      await signInWithGoogle();
    } catch {
      setAuthLoading(false);
      localStorage.removeItem("just_clicked_login");
    }
  };

  const handleInitiateScan = async (e?: React.FormEvent, overrideUrl?: string) => {
    if (e) e.preventDefault();
    const targetUrl = overrideUrl || scanUrl;
    if (!targetUrl.trim()) return;
    
    let formattedUrl = targetUrl.trim();
    if (!/^https?:\/\//i.test(formattedUrl)) {
      formattedUrl = "https://" + formattedUrl;
    }
    
    setScanUrl(formattedUrl);
    setScanModalOpen(true);
    setScanStep(0);
    setLoadingStep(0);
    setScanError(null);
    setScanResult(null);

    const stepsTimer = setInterval(() => {
      setLoadingStep((prev) => {
        if (prev >= 4) {
          clearInterval(stepsTimer);
          return 4;
        }
        return prev + 1;
      });
    }, 1000);

    try {
      const { data, error } = await supabase.functions.invoke("live-check", {
        body: { url: formattedUrl }
      });

      if (error) throw error;
      setScanResult(data);
      setScanStep(1);
    } catch (err: any) {
      setScanError(err.message || "Failed to scan site. Verification timed out.");
      setScanStep(1);
    } finally {
      clearInterval(stepsTimer);
    }
  };

  const handleContinueWithGoogle = () => {
    localStorage.setItem("pending_monitor_url", scanUrl);
    if (scanResult) {
      localStorage.setItem("pending_monitor_scan", JSON.stringify(scanResult));
    }
    handleGoogleLogin();
  };

  const handleContinueWithEmail = () => {
    localStorage.setItem("pending_monitor_url", scanUrl);
    if (scanResult) {
      localStorage.setItem("pending_monitor_scan", JSON.stringify(scanResult));
    }
    navigate("/login");
  };

  const openWaitlist = (plan: string) => {
    setSelectedPlan(plan);
    setWaitlistOpen(true);
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "UptimeWing",
    "url": "https://uptimewing.com",
    "logo": "https://uptimewing.com/logo.png",
    "sameAs": [
      "https://x.com/uptimewing"
    ]
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "UptimeWing Pro",
    "description": "Premium incident diagnostics, Slack rollback triggers, and multi-channel notification routing.",
    "offers": {
      "@type": "Offer",
      "price": "49.00",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": "142"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How does the multi-region consensus engine work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "When a primary monitoring node detects that your target server is unresponsive, it queries secondary nodes in adjacent global zones. Our consensus algorithm requires a quorum of 2 out of 3 independent checkpoints confirming the failure before an incident is declared, eliminating false positives."
        }
      },
      {
        "@type": "Question",
        "name": "How does UptimeWing compare to UptimeRobot or Better Stack?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "UptimeWing offers 60-second checking intervals for free, whereas legacy tools charge high markups for this frequency. Additionally, UptimeWing actively focuses on remediation—automatically rebooting your server via Vercel, Render, or GitHub Actions when an outage is confirmed."
        }
      },
      {
        "@type": "Question",
        "name": "Can UptimeWing monitor private networks or internal staging environments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, using our Heartbeat (Push) monitoring. If your server is behind a firewall, it can periodically send a lightweight ping to a unique UptimeWing endpoint. If your server fails to check in within the configured grace period, the system alerts you."
        }
      }
    ]
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "UptimeWing",
    "url": "https://uptimewing.com"
  };  const [timelineItems, setTimelineItems] = useState([
    { time: "02:47:12", status: "checking", label: "API LATENCY SPIKE DETECTED", isNew: false },
    { time: "02:47:15", status: "checking", label: "CHECKING DATABASE METRICS...", isNew: false },
  ]);
  const [demoActive, setDemoActive] = useState(false);

  const triggerTestIncident = () => {
    if (demoActive) return;
    setDemoActive(true);
    
    // Ingest events step-by-step
    setTimeout(() => {
      setTimelineItems(prev => [
        ...prev,
        { time: "02:47:18", status: "info", label: "CORRELATING WITH DEPLOY 1A2B3C", isNew: true }
      ]);
    }, 1000);

    setTimeout(() => {
      setTimelineItems(prev => [
        ...prev,
        { time: "02:47:21", status: "resolved", label: "ROOT CAUSE: DB CPU 98% AFTER MIGRATION", isNew: true }
      ]);
    }, 2200);

    setTimeout(() => {
      setTimelineItems(prev => [
        ...prev,
        { time: "02:47:22", status: "resolved", label: "ALERT SENT TO #INCIDENTS", isNew: true }
      ]);
    }, 3400);
  };  return (
    <div className="min-h-screen bg-white text-black selection:bg-blue-500 selection:text-white font-sans overflow-x-hidden md:snap-y md:snap-mandatory">
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      <LandingHeader currentPath="/" />      <main className="w-full">
        <LandingHero user={user} onScanUrl={(url) => {
          handleInitiateScan(undefined, url);
        }} />
        <LandingAgitate />
        <LandingDualMode />
        <LandingMechanism />
        <LandingProof />



        <LandingPricing onSelectPlan={(plan) => {
          if (plan === "starter") {
            handleGoogleLogin();
          } else if (plan === "pro" || plan === "pro_monthly" || plan === "pro_annual") {
            const cycle = plan === "pro_annual" ? "annual" : "monthly";
            if (!user) {
              localStorage.setItem("just_clicked_login", "true");
              localStorage.setItem("pending_checkout_plan", "pro");
              localStorage.setItem("pending_checkout_cycle", cycle);
              signInWithGoogle();
            } else {
              navigate(`/pricing?cycle=${cycle}`);
            }
          } else {
            navigate("/pricing");
          }
        }} />
      </main>

      {/* SECTION 1: FAQ */}
      <section className="border-t-2 border-black bg-white py-20 select-none">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Column */}
          <div>
            <p className="font-mono text-sm mb-4">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-bold uppercase font-mono tracking-tight leading-none">FREQUENTLY<br/>ASKED<br/>QUESTIONS</h2>
            <p className="text-gray-700 mt-4 font-sans text-sm font-bold uppercase">Last doubts before you connect.</p>
          </div>

          {/* Right Column Accordion */}
          <div className="md:col-span-2 flex flex-col">
            <details className="border-2 border-black border-b-0 group" open>
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none select-none">
                <span className="font-mono text-sm font-bold uppercase">1. DO I NEED PAGERDUTY TO USE THIS?</span>
                <span className="font-mono text-lg font-black transition-none group-open:rotate-45">+</span>
              </summary>
              <div className="px-6 pb-6 pt-0 border-t-2 border-black font-sans text-xs text-gray-700 uppercase font-bold leading-relaxed">
                No. We have built-in monitoring for side projects. But if you have PagerDuty, we plug in and tell you WHY it fired. That's where the magic is.
              </div>
            </details>

            <details className="border-2 border-black border-b-0 group">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none select-none">
                <span className="font-mono text-sm font-bold uppercase">2. WHAT'S THE DIFFERENCE VS UPTIME ROBOT?</span>
                <span className="font-mono text-lg font-black transition-none group-open:rotate-45">+</span>
              </summary>
              <div className="px-6 pb-6 pt-0 border-t-2 border-black font-sans text-xs text-gray-700 uppercase font-bold leading-relaxed">
                Uptime Robot: "Your site is down." UptimeWing: "Your site is down BECAUSE deploy #882bcf broke it. Run: vercel rollback #882bce" We don't monitor. We diagnose.
              </div>
            </details>

            <details className="border-2 border-black border-b-0 group">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none select-none">
                <span className="font-mono text-sm font-bold uppercase">3. WHICH TOOLS DO YOU SUPPORT?</span>
                <span className="font-mono text-lg font-black transition-none group-open:rotate-45">+</span>
              </summary>
              <div className="px-6 pb-6 pt-0 border-t-2 border-black font-sans text-xs text-gray-700 uppercase font-bold leading-relaxed">
                Inputs: PagerDuty, Datadog, Grafana, New Relic, Sentry. Context: GitHub, GitLab, Vercel, AWS. Outputs: Slack, Linear, Jira, Webhooks.
              </div>
            </details>

            <details className="border-2 border-black border-b-0 group">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none select-none">
                <span className="font-mono text-sm font-bold uppercase">4. HOW MUCH DOES IT COST?</span>
                <span className="font-mono text-lg font-black transition-none group-open:rotate-45">+</span>
              </summary>
              <div className="px-6 pb-6 pt-0 border-t-2 border-black font-sans text-xs text-gray-700 uppercase font-bold leading-relaxed">
                Free tier is $0 forever for 1 app. Pro is $49/mo for up to 5 apps. Lifetime Pro Deal is $499 once forever.
              </div>
            </details>

            <details className="border-2 border-black border-b-0 group">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none select-none">
                <span className="font-mono text-sm font-bold uppercase">5. WHAT HAPPENS AFTER AN INCIDENT OCCURS?</span>
                <span className="font-mono text-lg font-black transition-none group-open:rotate-45">+</span>
              </summary>
              <div className="px-6 pb-6 pt-0 border-t-2 border-black font-sans text-xs text-gray-700 uppercase font-bold leading-relaxed">
                Every tier has unlimited AI analysis. Sentry + Datadog + GitHub triggers will be parsed, analyzed, and returned with a clear, actionable fix command directly inside your Slack or email alerts. No limits, no quota anxiety.
              </div>
            </details>

            <details className="border-2 border-black border-b-0 group">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none select-none">
                <span className="font-mono text-sm font-bold uppercase">6. IS MY DATA SECURE?</span>
                <span className="font-mono text-lg font-black transition-none group-open:rotate-45">+</span>
              </summary>
              <div className="px-6 pb-6 pt-0 border-t-2 border-black font-sans text-xs text-gray-700 uppercase font-bold leading-relaxed">
                Yes. We never clone or access your raw code repositories. We only read incident error traces and deployment metadata via secure OAuth tokens from Sentry, GitHub, and Datadog. Telemetry is encrypted in transit and at rest.
              </div>
            </details>

            <details className="border-2 border-black last:border-b-2 group">
              <summary className="flex justify-between items-center p-6 cursor-pointer list-none select-none">
                <span className="font-mono text-sm font-bold uppercase">7. WHAT IF THE AI FAILS TO FIND A FIX AT 2AM?</span>
                <span className="font-mono text-lg font-black transition-none group-open:rotate-45">+</span>
              </summary>
              <div className="px-6 pb-6 pt-0 border-t-2 border-black font-sans text-xs text-gray-700 uppercase font-bold leading-relaxed">
                If our AI correlation engines can't isolate a root cause with absolute confidence, we'll still deliver the raw error log metrics directly to your Slack channel immediately. We never guess, and your connected telemetry nodes continue tracking normally.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* SECTION 2: CTA BANNER */}
      <section className="bg-black text-white py-28 border-y-2 border-black select-none relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '16px 16px' }}></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10 font-mono">
          <h2 className="text-5xl md:text-8xl font-black uppercase tracking-tighter leading-none mb-6">
            STOP DEBUGGING<br/>
            <span className="text-yellow-300">START FIXING</span>
          </h2>
          <p className="text-sm md:text-lg font-sans font-bold uppercase text-zinc-400 mb-10 max-w-2xl mx-auto tracking-wide leading-relaxed">
            Connect your stack in 90 seconds. Get your first answer tonight.
          </p>
          <div className="inline-block group">
            <button 
              onClick={handleGoogleLogin} 
              className="inline-block bg-yellow-300 text-black px-10 py-5 border-2 border-black font-mono text-base font-black uppercase shadow-[8px_8px_0_0_#fff] hover:shadow-[2px_2px_0_0_#fff] hover:translate-x-[6px] hover:translate-y-[6px] transition-all duration-150"
            >
              Start Free →
            </button>
          </div>
          <p className="font-mono text-xs text-zinc-500 mt-8 font-bold uppercase tracking-wider">
            1 app free forever. No credit card.
          </p>
        </div>
      </section>

      {/* SECTION 3: FOOTER */}
      <footer className="bg-white border-t-2 border-black py-16 select-none">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Column 1 - Brand */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 bg-black text-white flex items-center justify-center p-1 border border-black">
                  <UptimeWingLogo className="h-full w-full" />
                </div>
                <p className="font-mono text-sm font-black uppercase tracking-tight">UPTIMEWING</p>
              </div>
              <p className="text-[10px] font-mono font-bold uppercase text-gray-600 leading-relaxed">Incident Intelligence Layer<br/>From page to fix in 30 seconds.</p>
            </div>

            {/* Column 2 - Compare */}
            <div>
              <p className="font-mono text-[10px] font-black text-zinc-400 uppercase tracking-widest mb-4">Compare</p>
              <ul className="space-y-2 font-mono text-[10px] uppercase font-bold text-zinc-650">
                <li><Link to="/vs/pagerduty" className="hover:text-black">vs PagerDuty</Link></li>
                <li><Link to="/vs/uptimerobot" className="hover:text-black">vs UptimeRobot</Link></li>
                <li><Link to="/vs/better-stack" className="hover:text-black">vs BetterStack</Link></li>
                <li><Link to="/vs/pingdom" className="hover:text-black">vs Pingdom</Link></li>
                <li><Link to="/vs/uptimekuma" className="hover:text-black">vs UptimeKuma</Link></li>
              </ul>
            </div>
            <div>
              <p className="font-mono text-xs font-bold uppercase mb-4">PRODUCT</p>
              <ul className="space-y-2 text-xs font-bold uppercase font-sans text-zinc-800">
                <li><Link to="/pricing" className="hover:underline">Pricing</Link></li>
                <li><Link to="/changelog" className="hover:underline">Changelog</Link></li>
                <li><Link to="/status" className="hover:underline">Status</Link></li>
                <li><Link to="/integrations" className="hover:underline">Integrations</Link></li>
              </ul>
            </div>

            {/* Column 3 - Company */}
            <div>
              <p className="font-mono text-xs font-bold uppercase mb-4">COMPANY</p>
              <ul className="space-y-2 text-xs font-bold uppercase font-sans text-zinc-800">
                <li><Link to="/blog" className="hover:underline">Blog</Link></li>
                <li><Link to="/about" className="hover:underline">About</Link></li>
                <li><a href="mailto:founders@uptimewing.com" className="hover:underline">Contact</a></li>
                <li><Link to="/careers" className="hover:underline">Careers</Link></li>
              </ul>
            </div>

            {/* Column 4 - Free Tools */}
            <div>
              <p className="font-mono text-xs font-bold uppercase mb-4">FREE TOOLS</p>
              <ul className="space-y-2 text-xs font-bold uppercase font-sans text-zinc-800">
                <li><Link to="/tools" className="hover:underline text-black font-black">All Free Tools</Link></li>
                <li><Link to="/tools/cron-expression-generator" className="hover:underline">Cron Generator</Link></li>
                <li><Link to="/tools/ssl-expiry-checker" className="hover:underline">SSL Checker</Link></li>
                <li><Link to="/tools/dns-checker" className="hover:underline">DNS Checker</Link></li>
                <li><Link to="/tools/grok4-api-error-decoder" className="hover:underline">Grok Error Decoder</Link></li>
                <li><Link to="/tools/db-connection-parser" className="hover:underline">DB Conn Parser</Link></li>
                <li><Link to="/tools/env-validator" className="hover:underline">.env Validator</Link></li>
                <li><Link to="/tools/mttr-calculator" className="hover:underline">MTTR Calculator</Link></li>
              </ul>
            </div>

            {/* Column 5 - Legal */}
            <div>
              <p className="font-mono text-xs font-bold uppercase mb-4">LEGAL</p>
              <ul className="space-y-2 text-xs font-bold uppercase font-sans text-zinc-800">
                <li><Link to="/privacy" className="hover:underline">Privacy</Link></li>
                <li><Link to="/terms" className="hover:underline">Terms</Link></li>
                <li><Link to="/security" className="hover:underline">Security</Link></li>
                <li><Link to="/dpa" className="hover:underline">DPA</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t-2 border-black pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-mono text-xs text-gray-500 font-bold">© 2026 UPTIMEWING, INC. ALL SYSTEMS OPERATIONAL.</p>
            <div className="flex gap-6">
              <a href="https://twitter.com/uptimewing" target="_blank" rel="noopener noreferrer" className="font-mono text-xs hover:underline font-bold">TWITTER</a>
              <a href="https://github.com/uptimewing" target="_blank" rel="noopener noreferrer" className="font-mono text-xs hover:underline font-bold">GITHUB</a>
              <Link to="/login" className="font-mono text-xs hover:underline font-bold">LOGIN →</Link>
            </div>
          </div>
        </div>
      </footer>

      <WaitlistModal
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        initialPlan={selectedPlan}
      />

      <Dialog open={scanModalOpen} onOpenChange={setScanModalOpen}>
        <DialogContent className="sm:max-w-md rounded-none border-4 border-foreground shadow-[8px_8px_0_0_#000] p-8 bg-background text-foreground select-none">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl font-black uppercase tracking-tighter flex items-center gap-2">
              <Terminal className="h-6 w-6 text-primary" /> Checking your site...
            </DialogTitle>
          </DialogHeader>

          <div className="py-6 font-mono text-sm">
            {scanStep === 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  {loadingStep >= 0 ? (
                    loadingStep > 0 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    )
                  ) : null}
                  <span className={loadingStep === 0 ? "text-foreground font-bold" : "text-muted-foreground"}>Checking DNS...</span>
                </div>
                <div className="flex items-center gap-3">
                  {loadingStep >= 1 ? (
                    loadingStep > 1 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    )
                  ) : null}
                  <span className={loadingStep === 1 ? "text-foreground font-bold" : "text-muted-foreground"}>Checking SSL...</span>
                </div>
                <div className="flex items-center gap-3">
                  {loadingStep >= 2 ? (
                    loadingStep > 2 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    )
                  ) : null}
                  <span className={loadingStep === 2 ? "text-foreground font-bold" : "text-muted-foreground"}>Checking Response Time...</span>
                </div>
                <div className="flex items-center gap-3">
                  {loadingStep >= 3 ? (
                    loadingStep > 3 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    )
                  ) : null}
                  <span className={loadingStep === 3 ? "text-foreground font-bold" : "text-muted-foreground"}>Checking Global Reachability...</span>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {scanError ? (
                  <div className="border-4 border-foreground p-4 bg-status-down/10 text-status-down space-y-2 text-left">
                    <div className="text-sm font-black uppercase truncate">{scanUrl.replace(/^https?:\/\//i, "")}</div>
                    <div className="text-xs font-black uppercase">🔴 Scan Failed</div>
                    <p className="text-[10px] leading-relaxed font-bold">{scanError}</p>
                  </div>
                ) : scanResult ? (
                  <div className="border-4 border-foreground p-4 bg-accent/40 space-y-3 text-left">
                    <div className="text-sm font-black uppercase text-foreground truncate border-b border-dashed border-foreground/30 pb-1.5">
                      {scanUrl.replace(/^https?:\/\//i, "")}
                    </div>
                    <div className="space-y-1 text-xs uppercase font-black">
                      <div className={scanResult.status === "passing" || scanResult.status === "up" ? "text-green-500 flex items-center gap-1.5" : "text-red-500 flex items-center gap-1.5"}>
                        <span>{scanResult.status === "passing" || scanResult.status === "up" ? "✓" : "✗"}</span>
                        <span>Status: {scanResult.statusCode ? `${scanResult.statusCode} OK` : scanResult.status}</span>
                      </div>
                      <div className="text-foreground flex items-center gap-1.5">
                        <span>⚡</span>
                        <span>Response: {scanResult.responseTime}ms</span>
                      </div>
                      <div className="text-foreground flex items-center gap-1.5">
                        <span>🛡️</span>
                        <span>SSL check completed</span>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-3">
                  <p className="text-xs font-bold text-muted-foreground uppercase leading-relaxed text-left border-b-2 border-foreground/15 pb-1">
                    Save this monitor and we'll watch it 24/7
                  </p>
                  <Button
                    onClick={handleContinueWithGoogle}
                    className="w-full h-12 rounded-none border-2 border-foreground bg-primary text-primary-foreground hover:bg-foreground hover:text-background font-black uppercase tracking-tighter shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all flex items-center justify-center gap-2.5 text-xs"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" aria-hidden="true">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
